import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { gsap } from "gsap";
import { usePostHog } from 'posthog-js/react';
import { captureEvent, captureError } from "../utils/posthogUtils";
import "./AddTaskModal.css";
import "./ResponsiveModal.css";
import Select from "react-select";
import getApiUrl from "../utils/api";

const normalizeDateInputValue = (value) => {
  if (!value) {
    return "";
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? ""
      : value.toISOString().split("T")[0];
  }

  const raw = String(value).trim();
  if (!raw) {
    return "";
  }

  const isoMatch = raw.match(/^\d{4}-\d{2}-\d{2}/);
  if (isoMatch) {
    return isoMatch[0];
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().split("T")[0];
};

const EditTaskModal = ({
  open,
  onClose,
  onTaskUpdated,
  task,
  employees = [],
}) => {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);

  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [description, setDescription] = useState("");
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [status, setStatus] = useState("Pending");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [employeeList, setEmployeeList] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const posthog = usePostHog();

  const extractAssigneeIds = useCallback((incomingTask) => {
    if (!incomingTask) return [];
    const ids = new Set();

    if (Array.isArray(incomingTask.assignees)) {
      incomingTask.assignees.forEach((assignee) => {
        const candidate =
          assignee?.userId ??
          assignee?.user_id ??
          assignee?.assigned_to ??
          assignee?.id;
        if (candidate !== null && candidate !== undefined) {
          ids.add(String(candidate));
        }
      });
    }

    if (
      incomingTask.assigned_to !== null &&
      incomingTask.assigned_to !== undefined
    ) {
      ids.add(String(incomingTask.assigned_to));
    }

    return Array.from(ids);
  }, []);

  useEffect(() => {
    if (task) {
      setTitle(task.title || "");
      setPriority(task.priority || "");

      const rawProjectId =
        task.project_id ??
        task.projectId ??
        (typeof task.project === "object" && task.project
          ? task.project.id ?? task.project.project_id
          : undefined);

      setProjectId(
        rawProjectId !== null && rawProjectId !== undefined
          ? String(rawProjectId)
          : ""
      );

      const incomingProjectName =
        task?.project_name ||
        (task?.project && typeof task.project === "object"
          ? task.project.name
          : undefined) ||
        (typeof task?.project === "string" ? task.project : "");

      setProjectName(incomingProjectName || "");
      setTargetDate(normalizeDateInputValue(task.due_date));
      setStartDate(normalizeDateInputValue(task.start_date));
      setDescription(
        task.originalDescription ?? task.description ?? ""
      );
      setSelectedAssignees(extractAssigneeIds(task));
      setStatus(task.status || "Pending");
      setError("");
    } else {
      setTitle("");
      setPriority("");
      setProjectId("");
      setProjectName("");
      setTargetDate("");
      setStartDate("");
      setDescription("");
      setSelectedAssignees([]);
      setStatus("Pending");
      setError("");
    }
  }, [extractAssigneeIds, task]);

  useEffect(() => {
    if (open) {
      // Show modal with animation
      gsap.set(overlayRef.current, { display: "flex" });
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: "power2.out" }
      );

      gsap.fromTo(
        contentRef.current,
        {
          opacity: 0,
          y: -50,
          scale: 0.9,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.4,
          ease: "back.out(1.7)",
          delay: 0.1,
        }
      );

      setLoadingEmployees(true);
      fetch(getApiUrl("api/auth/employees"), { credentials: "include" })
        .then((res) => (res.ok ? res.json() : []))
        .then((data) => {
          setEmployeeList(Array.isArray(data) ? data : []);
          setLoadingEmployees(false);
        })
        .catch((err) => {
          console.error("Failed to fetch employees", err);
          setEmployeeList([]);
          setLoadingEmployees(false);
        });

      setLoadingProjects(true);
      fetch(getApiUrl("api/project"), { credentials: "include" })
        .then((res) => (res.ok ? res.json() : []))
        .then((data) => {
          setProjects(Array.isArray(data) ? data : []);
          setLoadingProjects(false);
        })
        .catch((err) => {
          console.error("Failed to fetch projects", err);
          setProjects([]);
          setLoadingProjects(false);
        });

      // Resolve current user for permissions (assignee-only status change)
      fetch(getApiUrl("api/auth/me"), { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) =>
          setCurrentUserId(
            d?.user?.userId || d?.user?.id || d?.user?.sub || null
          )
        )
        .catch(() => { });
    } else {
      // Hide modal with animation
      gsap.to(contentRef.current, {
        opacity: 0,
        y: -30,
        scale: 0.95,
        duration: 0.2,
        ease: "power2.in",
      });

      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.in",
        delay: 0.1,
        onComplete: () => {
          gsap.set(overlayRef.current, { display: "none" });
        },
      });
    }
  }, [open]);

  useEffect(() => {
    if (Array.isArray(employees)) {
      setEmployeeList(employees);
    }
  }, [employees]);

  const handleClose = () => {
    // Animate close before calling onClose
    gsap.to(contentRef.current, {
      opacity: 0,
      y: -30,
      scale: 0.95,
      duration: 0.2,
      ease: "power2.in",
    });

    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      delay: 0.1,
      onComplete: () => {
        gsap.set(overlayRef.current, { display: "none" });
        onClose();
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!task?.id) {
      setError("Task reference is missing.");
      return;
    }

    setLoading(true);

    try {
      const previousStatus = task?.status || "";
      const currentUser = JSON.parse(localStorage.getItem("user")) || {};
      const currentUserIdLocal = currentUser?.userId || currentUser?.id;
      const effectiveUserId = currentUserId || currentUserIdLocal;
      const isAssignee =
        task &&
        effectiveUserId &&
        selectedAssignees.some(
          (assigneeId) => String(assigneeId) === String(effectiveUserId)
        );
      const normalizedAssignees = selectedAssignees.filter(Boolean);
      if (normalizedAssignees.length === 0) {
        setError("Please select at least one assignee.");
        setLoading(false);
        return;
      }

      const normalizedAssignedTo = normalizedAssignees[0] || null;
      const normalizedProjectId = projectId || null;

      const sanitizedDueDate = targetDate ? targetDate.trim() : "";
      const sanitizedStartDate = startDate ? startDate.trim() : "";

      const requestBody = {
        title,
        priority: priority,
        project_id: normalizedProjectId || null,
        due_date: sanitizedDueDate || null,
        start_date: sanitizedStartDate || null,
        description,
        assigned_to: normalizedAssignedTo || null,
        assignees: normalizedAssignees,
        group_id: task?.group_id || null,
        // Only assignee can change status; editors cannot
        status: isAssignee ? status : previousStatus,
        department_id: null,
        kpi: null,
        currentUserId: effectiveUserId,
      };

      const response = await fetch(getApiUrl(`api/task/${task.id}`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      if (response.status === 200) {
        captureEvent(posthog, 'Task Updated', { taskId: task.id, title });
        if (onTaskUpdated) onTaskUpdated({
          oldStatus: task?.status || "Pending",
          status: status
        });
        // Server will create notifications (updated/status-changed/reassigned)
        handleClose();
        return;
      }

      if (!response.ok) {
        let message = "Failed to update task";
        try {
          const errorData = await response.json();
          message = errorData.error || errorData.message || message;
        } catch (parseError) {
          try {
            const text = await response.text();
            if (text) {
              message = text;
            }
          } catch { }
        }
        throw new Error(message);
      }

      if (onTaskUpdated) onTaskUpdated({
        oldStatus: task?.status || "Pending",
        status: status
      });
      // Notifications are handled by the server; no client emit to avoid duplicates
      handleClose();
    } catch (err) {
      captureError(posthog, 'Task Update Failed', err, { taskId: task?.id, title });
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const employeeOptions = useMemo(
    () =>
      employeeList.map((emp) => ({
        value: String(emp.id),
        label: `${emp.FirstName} ${emp.LastName} (${emp.email})`,
      })),
    [employeeList]
  );

  const fallbackAssigneeLabels = useMemo(() => {
    const map = new Map();
    if (Array.isArray(task?.assignees)) {
      task.assignees.forEach((assignee) => {
        const id =
          assignee?.userId ??
          assignee?.user_id ??
          assignee?.assigned_to ??
          assignee?.id;
        if (id !== null && id !== undefined) {
          const labelCandidate =
            assignee?.name ||
            `${assignee?.FirstName || assignee?.first_name || ""} ${assignee?.LastName || assignee?.last_name || ""
              }`.trim() ||
            assignee?.email ||
            `User ${id}`;
          map.set(String(id), labelCandidate);
        }
      });
    }
    if (task?.assigned_to !== null && task?.assigned_to !== undefined) {
      const fallbackLabel =
        task?.assignee_name ||
        `${task?.FirstName || ""} ${task?.LastName || ""}`.trim() ||
        task?.email ||
        `User ${task.assigned_to}`;
      map.set(String(task.assigned_to), fallbackLabel);
    }
    return map;
  }, [task]);

  const selectedAssigneeOptions = useMemo(() => {
    const optionMap = new Map(
      employeeOptions.map((option) => [String(option.value), option])
    );

    return selectedAssignees.map((assigneeId) => {
      const key = String(assigneeId);
      if (optionMap.has(key)) {
        return optionMap.get(key);
      }
      const label = fallbackAssigneeLabels.get(key) || `User ${key}`;
      return { value: key, label };
    });
  }, [employeeOptions, fallbackAssigneeLabels, selectedAssignees]);

  const projectOptions = useMemo(() => {
    const base = Array.isArray(projects)
      ? projects.map((proj) => ({
        value: String(proj.id),
        label: proj.name || `Project #${proj.id}`,
      }))
      : [];

    if (projectId && base.every((opt) => opt.value !== projectId)) {
      base.unshift({
        value: String(projectId),
        label: projectName || `Project #${projectId}`,
      });
    }

    return base;
  }, [projects, projectId, projectName]);

  const statusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "Planned", label: "Planned" },
    { value: "WIP", label: "WIP" },
    { value: "Completed", label: "Completed" },
    { value: "Revalidate", label: "Revalidate" },
    { value: "Delegated", label: "Delegated" },
    { value: "Reopen", label: "Reopen" },
  ];

  return (
    <div
      ref={overlayRef}
      className="modal-overlay modal-overlay-responsive"
      style={{ display: "none" }}
    >
      <div ref={contentRef} className="modal-content modal-content-responsive">
        <button
          className="modal-close modal-close-responsive"
          onClick={handleClose}
          type="button"
        >
          &times;
        </button>
        <div className="modal-header-responsive gradient-header">
          <h2 className="modal-title-responsive">Edit Task</h2>
          <div className="modal-subtitle">Update the task details</div>
        </div>
        <div className="modal-body-responsive modal-scrollable-content">
          <form
            id="edit-task-form"
            className="add-task-form modal-form-responsive"
            onSubmit={handleSubmit}
          >
            <label>
              <span className="label-row">
                Task Title<span className="required">*</span>
              </span>
              <input
                type="text"
                placeholder="Task Title"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <div className="modal-row modal-form-row-responsive">
              <label>
                <span className="label-row">
                  Task Type<span className="required">*</span>
                </span>
                <input
                  type="text"
                  placeholder="Select Task Type"
                  required
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                />
              </label>

              <label>
                <span className="label-row">
                  Status<span className="required">*</span>
                </span>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={statusOptions}
                  value={
                    statusOptions.find((opt) => opt.value === status) || null
                  }
                  onChange={(option) =>
                    setStatus(option ? option.value : "Pending")
                  }
                  placeholder="Select status..."
                  styles={{
                    container: (base) => ({
                      ...base,
                      marginTop: "2px",
                    }),
                  }}
                />
              </label>

            </div>
            <div className="modal-row modal-form-row-responsive">
              <label>
                <span className="label-row">Target End Date</span>
                <input
                  type="date"
                  placeholder="Select a date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                />
              </label>
              <label>
                <span className="label-row">Start Date</span>
                <input
                  type="date"
                  placeholder="Select a start date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
            </div>

            <label>
              <span className="label-row">Project</span>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={projectOptions}
                value={
                  projectOptions.find((opt) => opt.value === projectId) ||
                  null
                }
                onChange={(option) => {
                  if (option && option.value != null) {
                    setProjectId(String(option.value));
                    setProjectName(option.label || "");
                  } else {
                    setProjectId("");
                    setProjectName("");
                  }
                }}
                placeholder="Select Project"
                isLoading={loadingProjects}
                isSearchable
                isClearable
                styles={{
                  container: (base) => ({
                    ...base,
                    marginTop: "2px",
                  }),
                }}
              />
            </label>


            <div
              style={{
                fontWeight: 500,
                color: "#23272f",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                marginBottom: 0,
                position: "relative",
                marginTop: "4px",
              }}
            >
              <span className="label-row">
                Assign Employee<span className="required">*</span>
              </span>

              <button
                type='button'
                style={{
                  background: "none",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  fontSize: "12px",
                  color: currentUserId ? "#3b82f6" : "#9ca3af",
                  cursor: currentUserId ? "pointer" : "not-allowed",
                  transition: "all 0.2s",
                }}
                className="assign-to-me-button"
                disabled={!currentUserId}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!currentUserId) return;

                  const userIdStr = String(currentUserId);
                  const isAssigned = selectedAssignees.some(
                    (id) => String(id) === userIdStr
                  );

                  if (isAssigned) {
                    setSelectedAssignees((prev) =>
                      prev.filter((id) => String(id) !== userIdStr)
                    );
                  } else {
                    setSelectedAssignees((prev) => [...prev, userIdStr]);
                  }
                }}
              >
                {selectedAssignees.some(
                  (id) => String(id) === String(currentUserId)
                )
                  ? "Unassign Me"
                  : "Assign to Me"}
              </button>

              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={employeeOptions}
                value={selectedAssigneeOptions}
                onChange={(options) => {
                  if (!options) {
                    setSelectedAssignees([]);
                    return;
                  }
                  const next = Array.isArray(options)
                    ? options.map((option) => String(option.value))
                    : [];
                  setSelectedAssignees(next);
                }}
                isLoading={loadingEmployees}
                placeholder={
                  loadingEmployees
                    ? "Loading employees..."
                    : "Select one or more employees..."
                }
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                noOptionsMessage={() => "No employees found"}
                styles={{
                  container: (base) => ({
                    ...base,
                    marginTop: "2px",
                  }),
                }}
              />
            </div>

            <label>
              <span className="label-row">Description</span>
              <textarea
                placeholder="Add Description"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            {error && <div className="modal-error-responsive">{error}</div>}
          </form>
        </div>
        <div className="modal-footer-responsive">
          <button
            type="submit"
            form="edit-task-form"
            className="modal-btn-primary-responsive"
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Task"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTaskModal;
